import React from 'react';
import { Box, Container, Typography, Grid, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LinkOff,
  BugReport,
  DesignServices,
  AccessTime,
  Description,
  Psychology
} from '@mui/icons-material';

const problems = [
  {
    icon: <LinkOff />,
    title: 'Disconnected Tools',
    description: 'PRDs in Docs, tasks in JIRA, designs in Figma—nothing talks to each other.'
  },
  {
    icon: <BugReport />,
    title: 'Escaped Bugs',
    description: 'Context gets lost in handoffs, leading to bugs that should have been caught.'
  },
  {
    icon: <DesignServices />,
    title: 'Design Drift',
    description: 'Figma updates don\'t trigger test updates, creating silent gaps in coverage.'
  },
  {
    icon: <AccessTime />,
    title: 'Slow Onboarding',
    description: 'New QAs take weeks to learn the product because knowledge is scattered.'
  },
  {
    icon: <Description />,
    title: 'Stale Test Cases',
    description: 'Manual test cases are outdated the moment requirements change.'
  },
  {
    icon: <Psychology />,
    title: 'Manual RCA',
    description: 'Debugging takes hours of digging through docs instead of seconds.'
  }
];

const PainPointsGrid = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ py: 8, bgcolor: theme.palette.background.default }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 2,
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Why Teams Struggle Without Centralized Product Knowledge
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 400,
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            The old way of managing quality is broken. Here's what we fixed.
          </Typography>
        </Box>
        
        <Grid 
          container 
          spacing={3} 
          justifyContent="center"
        >
          {problems.map((problem, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: theme.palette.background.paper,
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: theme.shape.borderRadius * 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: `${theme.palette.primary.main}40`,
                    boxShadow: `0 0 20px ${theme.palette.primary.main}20`,
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ color: theme.palette.primary.main, display: 'flex', alignItems: 'center' }}>
                    {React.cloneElement(problem.icon, { fontSize: 'large' })}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                      margin: 0,
                    }}
                  >
                    {problem.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {problem.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PainPointsGrid;
